import { Button, Card, CardContent, Stack, Typography, Chip } from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import dayjs from "../../lib/dayjs";
import type { DoctorScheduleAppointment, NextAvailabilityLocal } from "../../services/doctors.service";

interface DoctorUpcomingAppointmentCardProps {
  appointment: DoctorScheduleAppointment;
  local: NextAvailabilityLocal | null;
  doctorId?: string;
  onOpenRecord?: (appointmentId: string, patientId: string, doctorId: string) => void;
  onOpenHistory?: (patientId: string) => void;
}

export function DoctorUpcomingAppointmentCard({ appointment, local, doctorId, onOpenRecord, onOpenHistory }: DoctorUpcomingAppointmentCardProps) {
  const start = dayjs(appointment.startTime);
  const end = dayjs(appointment.endTime);

  const statusColor: "default" | "success" | "warning" | "error" =
    appointment.status === "CONFIRMADO"
      ? "success"
      : appointment.status === "PENDENTE"
      ? "warning"
      : appointment.status === "CANCELADO"
      ? "error"
      : "default";

  const patientId = appointment.patient?.id ?? "";

  return (
    <Card sx={{ width: "100%", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={1.2}>
          <Typography variant="subtitle2" fontWeight={600} color="#133859">
            Consulta com {appointment.patient?.name || "Paciente não informado"}
          </Typography>

          {local && (
            <Stack spacing={0.3}>
              <Typography variant="caption" color="text.secondary">
                Local
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {local.localName || local.address || "Local não informado"}
              </Typography>
              {(local.city || local.state) && (
                <Typography variant="caption" color="text.secondary">
                  {local.city || ""}
                  {local.state ? ` / ${local.state}` : ""}
                </Typography>
              )}
            </Stack>
          )}

          <Stack direction="row" spacing={4} mt={1}>
            <Stack>
              <Typography variant="caption" color="text.secondary">
                Data
              </Typography>
              <Typography fontWeight={600} color="#133859">
                {start.format("DD/MM/YYYY")}
              </Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">
                Início
              </Typography>
              <Typography fontWeight={600} color="#133859">
                {start.format("HH:mm")}
              </Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">
                Fim
              </Typography>
              <Typography fontWeight={600} color="#133859">
                {end.format("HH:mm")}
              </Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Chip size="small" color={statusColor} label={appointment.status} />
            </Stack>
          </Stack>

          {(onOpenRecord || onOpenHistory) && (
            <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1}>
              {onOpenHistory && patientId && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<HistoryOutlinedIcon />}
                  onClick={() => onOpenHistory(patientId)}
                  sx={{ borderColor: "#133859", color: "#133859" }}
                >
                  Histórico Clínico
                </Button>
              )}
              {onOpenRecord && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ArticleOutlinedIcon />}
                  onClick={() =>
                    onOpenRecord(
                      appointment.id,
                      patientId,
                      doctorId ?? "",
                    )
                  }
                  sx={{ backgroundColor: "#133859" }}
                >
                  Prontuário
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
